import { ChevronLeft } from '@untitledui/icons';
import type { ReactNode } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

import type { ProposalDetail } from '../../lib/proposals/core/types';
import RecipientAvatar from './RecipientAvatar';

interface ActionDetailHeaderProps {
  action: ProposalDetail;
  /** The back chevron's action (mobile on a pane; every size on a page). */
  onClose: () => void;
  /**
   * Draw the bottom border. Default `true`. The proposal slideout hosts this
   * as `SlideoutMenu.Header` content beside the chrome's close button, and
   * draws the border on the header row instead so it spans the close too.
   */
  bordered?: boolean;
  /**
   * Right-aligned header content (the trigger context chips / "Context N"
   * pill). Visible at every size — the control collapses itself to a single
   * compact pill on narrow widths rather than disappearing.
   */
  trailing?: ReactNode;
  /**
   * Left-most header content, before the back chevron — used by the
   * full-page action route for its actions-list sidebar toggle. Rendered
   * ONLY when `variant` is `'page'`.
   *
   * The legacy actions-centre shell renders the list permanently beside the
   * detail pane, so a toggle there would be meaningless.
   */
  leading?: ReactNode;
  /**
   * Which surface is hosting this header.
   *
   * - `'pane'` (default) — the detail sits beside its list (the legacy
   *   actions-centre two-pane layout, or the record sidebar). The list is
   *   already on screen, so the back chevron is MOBILE-ONLY and the leading
   *   slot is suppressed.
   * - `'page'` — the full-page action route, where the detail is the whole
   *   view. The chevron is the only way back, so it shows at every size, and
   *   the leading slot renders.
   * - `'slideout'` — the proposal slideout, where this is the chrome's header
   *   content. The chrome renders the close button at the end of the row at
   *   every size, so no chevron renders at all.
   *
   * The close button is NOT this component's concern: on the slideout the
   * chrome (`SlideoutMenu.Header`) renders it, in every state; the pane and
   * the page have nothing to close.
   */
  variant?: 'pane' | 'page' | 'slideout';
}

export default function ActionDetailHeader({
  action,
  onClose,
  bordered = true,
  trailing,
  leading,
  variant = 'pane',
}: ActionDetailHeaderProps) {
  const isFullPage = variant === 'page';
  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) {
      return 'Just now';
    }
    if (diffInHours < 24) {
      return `Created ${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    return `Created ${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  };

  return (
    // border-b on the OUTER element so it runs the full page width; the inner
    // px-5 keeps the content inset without clipping the border.
    <div
      className={cx(
        'flex-shrink-0 bg-bg-primary relative',
        bordered && 'border-b border-border-secondary'
      )}
    >
      <div className='px-5'>
        {/* 72px + 1px border matches the chat conversation header exactly
            (ReasoningChat's breadcrumb row: py-4 + content = 72px content
            box). When the proposal pane docks beside the chat, the two
            header bottom borders form one continuous line — any height or
            border-token mismatch reads as a glitch there. Still taller than
            the 56px AI Actions list header; that mismatch was accepted long
            ago (a two-line title stack needs the room). */}
        <div className='h-[72px] flex items-center gap-2'>
          <div className='flex flex-row items-center gap-2 sm:gap-3 min-w-0 flex-1 overflow-hidden'>
            {isFullPage && leading}
            {/* Back button. On the full-page action route the chevron is the
                only way back to the list, so it shows at every size. On a
                'pane' surface the list is already beside the detail (legacy
                actions-centre), so the chevron is mobile-only. On the
                slideout the chrome's close is the exit at every size, so no
                chevron renders. */}
            {variant !== 'slideout' && (
              <Button
                color='tertiary'
                size='sm'
                iconLeading={ChevronLeft}
                onClick={onClose}
                className={
                  isFullPage
                    ? 'flex flex-shrink-0'
                    : 'flex lg:hidden flex-shrink-0'
                }
                aria-label='Back to actions list'
              />
            )}
            <RecipientAvatar action={action} size='sm' />
            <div className='min-w-0 flex-1 overflow-hidden'>
              <h2 className='text-sm sm:text-base font-semibold text-primary leading-tight mb-0.5 truncate'>
                {action.title}
              </h2>
              <p className='text-xs sm:text-sm text-secondary truncate'>
                {formatTimeAgo(action.created_at)}
              </p>
            </div>
          </div>

          {/* Right side: trigger context. Visible at every size — the
              control collapses itself to the compact "Context N" pill on
              narrow widths (the old `hidden sm:` gate hid the proposal's
              context entirely on mobile). */}
          {trailing && (
            <div className='flex items-center flex-shrink-0'>{trailing}</div>
          )}
        </div>
      </div>
    </div>
  );
}
